/* eslint-disable no-console */
import {HttpErrorResponse} from '@angular/common/http';
import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import {Store} from '@ngrx/store';
import {AddressDto, IAddress} from '@sportngin/se-commerce-sdk';
import {
  Observable,
  catchError,
  delay,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {AddressApiService, AddressMismatchReason} from '../../data/api/service';
import {AddressFormGroup, ControlsOf} from '../../data/model';
import {addSuggestedAddress} from '../address.actions';

export const zipCodeRegex = /^(?:\d{4}|\d{5}(?:-?\d{4})?)$/;

interface VerifiedAddressResult {
  address: IAddress;
  needsSuggestion: boolean;
  mismatchReasons?: readonly AddressMismatchReason[];
}

export class AddressValidator {
  static validateAddress(
    addressApiService: AddressApiService,
    store?: Store,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const addressFormGroup = control.value as AddressFormGroup;
      const enteredAddress: AddressDto = {
        address1: addressFormGroup.address1 || '',
        address2: addressFormGroup.address2 || '',
        city: addressFormGroup.city || '',
        state: addressFormGroup.state || '',
        zip: addressFormGroup.zip || null,
        country: addressFormGroup.country || '',
      };

      return AddressValidator.validateAddressWithDelay(
        enteredAddress,
        addressApiService,
        control,
        store,
      );
    };
  }

  /**
   * Validates an address with a delay to prevent excessive API calls, using address validation service.
   *
   * @param enteredAddress - The shopper-entered address sent for validation
   * @param addressApiService - Service for address validation API calls
   * @param formGroup - The form control group containing address fields
   * @param store - Optional store instance for state management
   *
   * @returns An Observable that emits validation errors or null if validation succeeds
   *
   * @remarks
   * The validation process includes:
   * - 350ms delay between validation attempts
   * - Retries up to 3 times on empty responses
   * - Validates ZIP code against suggested address from API
   * - Validates city and state against suggested address from API (authoritative source)
   * - Sets appropriate form errors for invalid fields
   * - Updates store with validated address if provided
   *
   * @throws Propagates HTTP errors
   */
  private static validateAddressWithDelay(
    enteredAddress: AddressDto,
    addressApiService: AddressApiService,
    formGroup: AbstractControl,
    store?: Store,
  ): Observable<ValidationErrors | null> {
    return of(enteredAddress).pipe(
      distinctUntilChanged(),
      switchMap((address: AddressDto) => {
        formGroup.setErrors(null);
        formGroup.markAsUntouched();

        return of(null).pipe(
          delay(350),
          switchMap(() =>
            AddressValidator.tryValidateAddress(address, addressApiService, 2),
          ),
        );
      }),
      map(verificationResult => {
        if (!verificationResult) {
          console.error(
            'Address validation failed: No suggested address returned',
          );
          return AddressValidator.setFormErrors(
            new Error(
              'Address validation failed: No suggested address returned',
            ),
            formGroup,
          );
        }

        const {
          address: suggestedAddress,
          mismatchReasons,
          needsSuggestion,
        } = verificationResult;
        const enteredZip = enteredAddress.zip;
        const suggestedZip = suggestedAddress?.zip ?? null;
        // Always treat a changed ZIP as corrected; OR in the reason when provided
        // so we still catch corrections if the backend omits or renames reasons.
        const zipCorrected =
          enteredZip !== suggestedZip ||
          Boolean(mismatchReasons?.includes('ZIP_CORRECTED'));
        if (zipCorrected) {
          const zipControl = formGroup.get('zip');
          if (zipControl) {
            zipControl.setErrors({invalidZip: true, suggestedZip});
            zipControl.markAsTouched({onlySelf: true});
            zipControl.markAsDirty({onlySelf: true});
          }
          console.warn('ZIP code mismatch:', {enteredZip, suggestedZip});
          const errors = {invalidZip: true, suggestedZip, suggestedAddress};
          if (store) {
            AddressValidator.dispatchAddress(
              formGroup,
              store,
              suggestedAddress,
            );
          }
          return errors;
        }

        // Validate state against suggestedAddress (authoritative source)
        const enteredState = enteredAddress.state?.toUpperCase().trim();
        const suggestedState = suggestedAddress?.state?.toUpperCase().trim();
        if (suggestedState && enteredState && enteredState !== suggestedState) {
          const stateControl = formGroup.get('state');
          if (stateControl) {
            stateControl.setErrors({invalidState: true});
            stateControl.markAsTouched({onlySelf: true});
            stateControl.markAsDirty({onlySelf: true});
          }
          console.warn('State mismatch:', {enteredState, suggestedState});
          return {invalidState: true};
        }

        // The API-returned verified city is authoritative.
        // Always treat a changed city as corrected; OR in the reason when provided
        // so we still catch corrections if the backend omits or renames reasons.
        const enteredCity = enteredAddress.city?.toLowerCase().trim();
        const suggestedCity = suggestedAddress?.city?.toLowerCase().trim();
        const cityCorrected =
          Boolean(
            suggestedCity && enteredCity && enteredCity !== suggestedCity,
          ) || Boolean(mismatchReasons?.includes('CITY_MISMATCH'));
        if (cityCorrected) {
          const cityControl = formGroup.get('city');
          if (cityControl) {
            cityControl.setErrors({invalidCity: true});
            cityControl.markAsTouched({onlySelf: true});
            cityControl.markAsDirty({onlySelf: true});
          }
          console.warn('City mismatch:', {enteredCity, suggestedCity});
          const errors = {invalidCity: true, suggestedAddress};
          if (store) {
            AddressValidator.dispatchAddress(
              formGroup,
              store,
              suggestedAddress,
            );
          }
          return errors;
        }

        if (store) {
          AddressValidator.dispatchAddress(formGroup, store, suggestedAddress);
        }
        return needsSuggestion
          ? {needsSuggestion: true, suggestedAddress}
          : null;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error during address validation:', error);
        return of(AddressValidator.setFormErrors(error, formGroup));
      }),
    );
  }

  /**
   * Projects an API response to verified address fields only.
   * The flat response is authoritative; wrapped shapes remain supported.
   */
  private static normalizeAddressResponse(
    response: unknown,
  ): VerifiedAddressResult | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const obj = response as Record<string, unknown>;
    const withAddress1 = (x: unknown): x is IAddress =>
      typeof x === 'object' &&
      x !== null &&
      'address1' in (x as Record<string, unknown>);
    const projectAddress = (value: unknown): IAddress | null => {
      let address: IAddress | null = null;

      if (withAddress1(value)) {
        address = {
          address1: value.address1,
          address2: value.address2,
          city: value.city,
          state: value.state,
          zip: value.zip,
          country: value.country,
        };
      }

      return address;
    };

    const address =
      projectAddress(obj) ??
      projectAddress(obj.data) ??
      projectAddress(obj.suggestedAddress);

    return address
      ? {
          address,
          needsSuggestion: obj.needsSuggestion === true,
          ...(Array.isArray(obj.mismatchReasons)
            ? {
                mismatchReasons:
                  obj.mismatchReasons as readonly AddressMismatchReason[],
              }
            : {}),
        }
      : null;
  }

  private static tryValidateAddress(
    address: AddressDto,
    addressApiService: AddressApiService,
    retriesLeft: number,
  ): Observable<VerifiedAddressResult | null> {
    return addressApiService.validateAddress(address).pipe(
      switchMap(response => {
        const suggested = AddressValidator.normalizeAddressResponse(response);
        if (!suggested) {
          if (retriesLeft > 0) {
            console.warn(
              `Blank response received. Retrying... (${retriesLeft} attempts left)`,
            );
            return of(null).pipe(
              delay(500 * Math.pow(2, 3 - retriesLeft)), // 500ms, 1000ms, 2000ms
              switchMap(() =>
                AddressValidator.tryValidateAddress(
                  address,
                  addressApiService,
                  retriesLeft - 1,
                ),
              ),
            );
          }
          console.error(
            'Address validation failed: No suggested address returned after retries',
          );
          return of(null);
        }
        return of(suggested);
      }),
      catchError(error => {
        console.error('Error during address validation:', error);
        return throwError(() => error);
      }),
    );
  }

  private static dispatchAddress(
    formGroup: AbstractControl,
    store: Store,
    suggestedAddress: IAddress,
  ): void {
    const firstName = formGroup.value.firstName;
    const lastName = formGroup.value.lastName;
    store.dispatch(
      addSuggestedAddress({
        address: {
          ...suggestedAddress,
          firstName,
          lastName,
        } as AddressFormGroup,
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  static setFormErrors(
    error: HttpErrorResponse | Error,
    abstractControl: AbstractControl<ControlsOf<AddressFormGroup>>,
  ): ValidationErrors | null {
    // Determine if it's an HttpErrorResponse or a generic Error
    const isHttpError = error instanceof HttpErrorResponse;

    // Only log errors if they're not expected validation responses
    if (isHttpError && error.status !== 400) {
      console.warn('Error received in setFormErrors:', error);
    } else if (!isHttpError) {
      console.warn('Generic error received in setFormErrors:', error);
    }

    const errorMappings: Record<
      string,
      {control?: keyof ControlsOf<AddressFormGroup>; errorType: string}
    > = {
      STATE_INVALID: {control: 'state', errorType: 'invalidState'},
      ZIP_INVALID: {control: 'zip', errorType: 'invalidZip'},
      ZIP_STATE_MISMATCH: {control: 'state', errorType: 'zipStateMismatch'},
      COUNTRY_INVALID: {control: 'country', errorType: 'invalidCountry'},
      CITY_INVALID: {control: 'city', errorType: 'invalidCity'},
      ADDRESS_INVALID: {errorType: 'invalidAddress'},
      CITY_STATE_INVALID: {control: 'zip', errorType: 'invalidCityState'},
      '010005': {errorType: 'invalidAddress'},
    };

    const errorCode = isHttpError ? error.error?.code : null;
    const errorInfo = errorMappings[
      errorCode as keyof typeof errorMappings
    ] || {
      errorType: 'invalidAddress',
    };

    // Set control errors if specified
    if (errorInfo.control) {
      const controlKey = errorInfo.control;
      abstractControl.get(controlKey)?.setErrors({
        [errorInfo.errorType]: true,
      });
      abstractControl.get(controlKey)?.markAsTouched({onlySelf: true});
    }

    let result: ValidationErrors;

    // Determine the appropriate error object
    if (isHttpError && error.status === 0) {
      result = {
        invalidAddress: true,
        errorCode: 'NETWORK_ERROR',
        message:
          'Address validation unavailable. Check network or CORS if using a different domain.',
      };
    } else if (isHttpError && error.status === 500) {
      result = {
        serverError: true,
        errorCode: 'SERVER_ERROR',
        message: 'Server error',
      };
    } else if (
      isHttpError &&
      error.error?.code === 'ADDRESS_INVALID' &&
      !error.error?.suggestedAddress
    ) {
      result = {
        invalidAddress: true,
        errorCode: error.error.code,
        message: 'Invalid address',
      };
    } else if (isHttpError && errorCode) {
      result = {
        [errorInfo.errorType]: true,
        errorCode: errorCode,
        message: error.error?.message,
      };
    } else {
      // Handle cases like "No suggested address returned" or generic errors
      result = {
        invalidAddress: true,
        message: isHttpError ? 'Address validation failed' : error.message,
      };
    }

    return result;
  }
}
